import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Search, TrendingUp, Target, BarChart3, Globe, Loader2, Plus, Trash2,
  CheckCircle2, AlertTriangle, XCircle, ArrowUp, ArrowDown, Minus,
  FileText, Sparkles, RefreshCw, Eye, ExternalLink, Lightbulb, Zap
} from 'lucide-react';
import { seoToolsAPI } from '../api';

const ScoreCircle = ({ score, size = 'lg' }) => {
  const getColor = () => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };
  
  const sizeClasses = size === 'lg' ? 'w-24 h-24' : 'w-16 h-16';
  const textSize = size === 'lg' ? 'text-2xl' : 'text-lg';
  
  return (
    <div className={`relative ${sizeClasses}`}>
      <svg className={`${sizeClasses} transform -rotate-90`}>
        <circle
          cx="50%"
          cy="50%"
          r="45%"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          className="text-slate-200"
        />
        <circle
          cx="50%"
          cy="50%"
          r="45%"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          strokeDasharray={`${score * 2.83} 283`}
          className={getColor()}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`${textSize} font-bold ${getColor()}`}>{score}</span>
      </div>
    </div>
  );
};

export const SEOToolsPage = () => {
  const { hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  
  // Keyword Research State
  const [seedKeyword, setSeedKeyword] = useState('');
  const [industry, setIndustry] = useState('');
  const [keywordContent, setKeywordContent] = useState('');
  const [keywordResults, setKeywordResults] = useState(null);
  
  // On-Page Analysis State
  const [pageTitle, setPageTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [pageContent, setPageContent] = useState('');
  const [targetKeywords, setTargetKeywords] = useState('');
  const [analysisResults, setAnalysisResults] = useState(null);
  
  // Rank Tracking State
  const [rankData, setRankData] = useState(null);
  const [showAddRankDialog, setShowAddRankDialog] = useState(false);
  const [newRankEntry, setNewRankEntry] = useState({ keyword: '', position: '', url: '' });
  
  // Competitors State
  const [competitors, setCompetitors] = useState([]);
  const [showAddCompetitorDialog, setShowAddCompetitorDialog] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({ domain: '', name: '' });
  
  // Content Optimization State
  const [optimizeContent, setOptimizeContent] = useState('');
  const [optimizeKeywords, setOptimizeKeywords] = useState('');
  const [contentType, setContentType] = useState('blog');
  const [optimizationResults, setOptimizationResults] = useState(null);
  
  // GSC Import State
  const [gscFile, setGscFile] = useState(null);
  const [gscImportResults, setGscImportResults] = useState(null);
  const [importingGsc, setImportingGsc] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await seoToolsAPI.getDashboard();
      setDashboard(res.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const handleKeywordResearch = async () => {
    if (!seedKeyword.trim()) {
      toast.error('Please enter a seed keyword');
      return;
    }
    
    try {
      setLoading(true);
      const res = await seoToolsAPI.researchKeywords({
        seed_keyword: seedKeyword,
        content: keywordContent || undefined,
        industry: industry || undefined
      });
      setKeywordResults(res.data);
      toast.success('Keyword research complete!');
    } catch (error) {
      toast.error('Failed to research keywords');
    } finally {
      setLoading(false);
    }
  };

  const handleOnPageAnalysis = async () => {
    if (!pageTitle.trim() || !pageContent.trim()) {
      toast.error('Please enter title and content');
      return;
    }
    
    try {
      setLoading(true);
      const res = await seoToolsAPI.analyzeOnPage({
        title: pageTitle,
        meta_description: metaDescription,
        content: pageContent,
        target_keywords: targetKeywords.split(',').map(k => k.trim()).filter(k => k)
      });
      setAnalysisResults(res.data);
      toast.success('Analysis complete!');
    } catch (error) {
      toast.error('Failed to analyze page');
    } finally {
      setLoading(false);
    }
  };

  const fetchRankTracking = async () => {
    try {
      const res = await seoToolsAPI.getRankTracking({ days: 30 });
      setRankData(res.data);
    } catch (error) {
      console.error('Failed to fetch rank data:', error);
    }
  };

  const handleAddRankEntry = async () => {
    if (!newRankEntry.keyword || !newRankEntry.position || !newRankEntry.url) {
      toast.error('Please fill all fields');
      return;
    }
    
    try {
      await seoToolsAPI.addRankEntry({
        keyword: newRankEntry.keyword,
        position: parseInt(newRankEntry.position),
        url: newRankEntry.url,
        search_engine: 'google'
      });
      toast.success('Rank entry added');
      setShowAddRankDialog(false);
      setNewRankEntry({ keyword: '', position: '', url: '' });
      fetchRankTracking();
    } catch (error) {
      toast.error('Failed to add entry');
    }
  };

  const fetchCompetitors = async () => {
    try {
      const res = await seoToolsAPI.getCompetitors();
      setCompetitors(res.data?.competitors || []);
    } catch (error) {
      console.error('Failed to fetch competitors:', error);
    }
  };

  const handleAddCompetitor = async () => {
    if (!newCompetitor.domain) {
      toast.error('Please enter domain');
      return;
    }
    
    try {
      await seoToolsAPI.addCompetitor(newCompetitor);
      toast.success('Competitor added');
      setShowAddCompetitorDialog(false);
      setNewCompetitor({ domain: '', name: '' });
      fetchCompetitors();
    } catch (error) {
      toast.error('Failed to add competitor');
    }
  };

  const handleDeleteCompetitor = async (id) => {
    try {
      await seoToolsAPI.deleteCompetitor(id);
      toast.success('Competitor removed');
      fetchCompetitors();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleContentOptimization = async () => {
    if (!optimizeContent.trim() || !optimizeKeywords.trim()) {
      toast.error('Please enter content and keywords');
      return;
    }
    
    try {
      setLoading(true);
      const res = await seoToolsAPI.optimizeContent({
        content: optimizeContent,
        target_keywords: optimizeKeywords.split(',').map(k => k.trim()).filter(k => k),
        content_type: contentType
      });
      setOptimizationResults(res.data);
      toast.success('Optimization analysis complete!');
    } catch (error) {
      toast.error('Failed to analyze content');
    } finally {
      setLoading(false);
    }
  };

  const handleGscImport = async () => {
    if (!gscFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    try {
      setImportingGsc(true);
      const res = await seoToolsAPI.importGSCCsv(gscFile);
      setGscImportResults(res.data);
      toast.success(`Imported ${res.data?.imported_count || 0} rank entries!`);
      setGscFile(null);
      fetchRankTracking();
    } catch (error) {
      toast.error('Failed to import GSC data');
    } finally {
      setImportingGsc(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="seo-tools-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Search className="h-6 w-6" />
            SEO Tools Suite
          </h1>
          <p className="text-muted-foreground">
            Keyword research, on-page analysis, rank tracking, and content optimization
          </p>
        </div>
        <Button variant="outline" onClick={fetchDashboard}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => {
        setActiveTab(v);
        if (v === 'rank-tracking') fetchRankTracking();
        if (v === 'competitors') fetchCompetitors();
      }}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="keywords">Keywords</TabsTrigger>
          <TabsTrigger value="on-page">On-Page</TabsTrigger>
          <TabsTrigger value="rank-tracking">Ranks</TabsTrigger>
          <TabsTrigger value="gsc-import">GSC Import</TabsTrigger>
          <TabsTrigger value="optimize">Optimize</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {dashboard && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Search className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                    <p className="text-2xl font-bold">{dashboard.stats?.keyword_researches || 0}</p>
                    <p className="text-sm text-muted-foreground">Keyword Researches</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Target className="h-8 w-8 mx-auto text-green-600 mb-2" />
                    <p className="text-2xl font-bold">{dashboard.stats?.keywords_tracked || 0}</p>
                    <p className="text-sm text-muted-foreground">Keywords Tracked</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <TrendingUp className="h-8 w-8 mx-auto text-amber-600 mb-2" />
                    <p className="text-2xl font-bold">{dashboard.stats?.avg_position || '-'}</p>
                    <p className="text-sm text-muted-foreground">Avg. Position</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Globe className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                    <p className="text-2xl font-bold">{dashboard.stats?.competitors_tracked || 0}</p>
                    <p className="text-sm text-muted-foreground">Competitors</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Recent Analyses</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard.recent_analyses?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboard.recent_analyses.map((analysis, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <p className="font-medium">{analysis.url || 'Page Analysis'}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(analysis.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge className={
                              analysis.grade === 'A' ? 'bg-green-100 text-green-700' :
                              analysis.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                              analysis.grade === 'C' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }>
                              Grade: {analysis.grade}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">
                        No analyses yet. Start with On-Page analysis!
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Top Keywords</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard.top_keywords?.length > 0 ? (
                      <div className="space-y-2">
                        {dashboard.top_keywords.slice(0, 5).map((kw, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 border rounded">
                            <span className="font-medium">{kw.keyword}</span>
                            <Badge variant="outline">Position: {kw.position}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">
                        No keywords tracked. Add rank entries!
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Keywords Tab */}
        <TabsContent value="keywords" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Keyword Research
              </CardTitle>
              <CardDescription>
                Find related keywords, search intent, and content ideas
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Seed Keyword *</Label>
                  <Input
                    placeholder="e.g., digital marketing"
                    value={seedKeyword}
                    onChange={(e) => setSeedKeyword(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Industry (optional)</Label>
                  <Input
                    placeholder="e.g., technology, healthcare"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Content to Analyze (optional)</Label>
                <Textarea
                  placeholder="Paste your content here to extract keywords..."
                  value={keywordContent}
                  onChange={(e) => setKeywordContent(e.target.value)}
                  rows={4}
                />
              </div>
              <Button onClick={handleKeywordResearch} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Research Keywords
              </Button>
            </CardContent>
          </Card>

          {keywordResults && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Extracted Keywords</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    {keywordResults.extracted_keywords?.length > 0 ? (
                      <div className="space-y-2">
                        {keywordResults.extracted_keywords.map((kw, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 border rounded">
                            <span>{kw.keyword}</span>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{kw.type}</Badge>
                              <Badge className={
                                kw.estimated_volume === 'high' ? 'bg-green-100 text-green-700' :
                                kw.estimated_volume === 'medium' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-700'
                              }>
                                {kw.frequency}x
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground">No content analyzed</p>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Suggested Keywords</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {keywordResults.suggested_keywords?.slice(0, 20).map((kw, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 border rounded">
                          <span className="text-sm">{kw.keyword}</span>
                          <Badge variant="outline" className="text-xs">{kw.intent}</Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {keywordResults.related_topics?.length > 0 && (
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="h-5 w-5" />
                      AI-Generated Topics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {keywordResults.related_topics.map((topic, idx) => (
                        <Badge key={idx} variant="secondary" className="text-sm">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* On-Page Analysis Tab */}
        <TabsContent value="on-page" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  On-Page SEO Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Page Title *</Label>
                  <Input
                    placeholder="Enter your page title"
                    value={pageTitle}
                    onChange={(e) => setPageTitle(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{pageTitle.length}/60 characters</p>
                </div>
                <div className="space-y-2">
                  <Label>Meta Description</Label>
                  <Textarea
                    placeholder="Enter meta description"
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    rows={2}
                  />
                  <p className="text-xs text-muted-foreground">{metaDescription.length}/160 characters</p>
                </div>
                <div className="space-y-2">
                  <Label>Target Keywords (comma-separated)</Label>
                  <Input
                    placeholder="keyword1, keyword2, keyword3"
                    value={targetKeywords}
                    onChange={(e) => setTargetKeywords(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Page Content *</Label>
                  <Textarea
                    placeholder="Paste your page content here..."
                    value={pageContent}
                    onChange={(e) => setPageContent(e.target.value)}
                    rows={8}
                  />
                  <p className="text-xs text-muted-foreground">{pageContent.split(' ').filter(w => w).length} words</p>
                </div>
                <Button onClick={handleOnPageAnalysis} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                  Analyze Page
                </Button>
              </CardContent>
            </Card>

            {analysisResults && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Analysis Results</CardTitle>
                    <div className="flex items-center gap-4">
                      <ScoreCircle score={analysisResults.score} />
                      <div>
                        <p className="text-3xl font-bold">{analysisResults.grade}</p>
                        <p className="text-sm text-muted-foreground">Grade</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="space-y-4">
                      {/* Issues */}
                      {analysisResults.issues?.length > 0 && (
                        <div>
                          <h4 className="font-medium text-red-600 flex items-center gap-2 mb-2">
                            <XCircle className="h-4 w-4" /> Issues ({analysisResults.issues.length})
                          </h4>
                          <div className="space-y-2">
                            {analysisResults.issues.map((issue, idx) => (
                              <div key={idx} className="p-3 bg-red-50 rounded-lg">
                                <p className="font-medium text-sm">{issue.issue}</p>
                                <p className="text-xs text-muted-foreground">{issue.recommendation}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Warnings */}
                      {analysisResults.warnings?.length > 0 && (
                        <div>
                          <h4 className="font-medium text-amber-600 flex items-center gap-2 mb-2">
                            <AlertTriangle className="h-4 w-4" /> Warnings ({analysisResults.warnings.length})
                          </h4>
                          <div className="space-y-2">
                            {analysisResults.warnings.map((warning, idx) => (
                              <div key={idx} className="p-3 bg-amber-50 rounded-lg">
                                <p className="font-medium text-sm">{warning.issue}</p>
                                <p className="text-xs text-muted-foreground">{warning.recommendation}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Passes */}
                      {analysisResults.passes?.length > 0 && (
                        <div>
                          <h4 className="font-medium text-green-600 flex items-center gap-2 mb-2">
                            <CheckCircle2 className="h-4 w-4" /> Passed ({analysisResults.passes.length})
                          </h4>
                          <div className="space-y-2">
                            {analysisResults.passes.map((pass, idx) => (
                              <div key={idx} className="p-2 bg-green-50 rounded-lg flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                <p className="text-sm">{pass.status}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Rank Tracking Tab */}
        <TabsContent value="rank-tracking" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Rank Tracking</h2>
            <Button onClick={() => setShowAddRankDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Entry
            </Button>
          </div>

          {rankData && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {rankData.keyword_stats?.slice(0, 6).map((stat, idx) => (
                  <Card key={idx}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{stat.keyword}</p>
                          <p className="text-2xl font-bold">#{stat.current_position}</p>
                        </div>
                        <div className={`flex items-center gap-1 ${
                          stat.trend === 'up' ? 'text-green-600' :
                          stat.trend === 'down' ? 'text-red-600' :
                          'text-slate-400'
                        }`}>
                          {stat.trend === 'up' ? <ArrowUp className="h-5 w-5" /> :
                           stat.trend === 'down' ? <ArrowDown className="h-5 w-5" /> :
                           <Minus className="h-5 w-5" />}
                          <span className="font-medium">{Math.abs(stat.change)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">All Entries</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    {rankData.entries?.length > 0 ? (
                      <div className="space-y-2">
                        {rankData.entries.map((entry, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <p className="font-medium">{entry.keyword}</p>
                              <p className="text-xs text-muted-foreground">{entry.url}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge>Position: {entry.position}</Badge>
                              <span className="text-xs text-muted-foreground">{entry.tracked_date}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">
                        No rank entries yet. Add your first keyword!
                      </p>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* GSC Import Tab */}
        <TabsContent value="gsc-import" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Import from Google Search Console
                </CardTitle>
                <CardDescription>
                  Upload your GSC Performance report CSV to import keyword rankings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setGscFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="gsc-file-input"
                  />
                  <label htmlFor="gsc-file-input" className="cursor-pointer">
                    <Globe className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="font-medium">Click to upload GSC CSV</p>
                    <p className="text-sm text-muted-foreground">
                      Export from GSC: Performance → Export → CSV
                    </p>
                  </label>
                  {gscFile && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto mb-1" />
                      <p className="text-sm font-medium text-green-800">{gscFile.name}</p>
                      <p className="text-xs text-green-600">{(gscFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  )}
                </div>
                
                <Button 
                  onClick={handleGscImport} 
                  disabled={!gscFile || importingGsc} 
                  className="w-full"
                >
                  {importingGsc ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TrendingUp className="h-4 w-4 mr-2" />
                  )}
                  Import Rankings
                </Button>

                <Separator />

                <div className="space-y-2">
                  <h4 className="font-medium text-sm">How to export from GSC:</h4>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Go to Google Search Console</li>
                    <li>Select Performance → Search Results</li>
                    <li>Set your date range (last 28 days recommended)</li>
                    <li>Click Export → Download CSV</li>
                    <li>Upload the file here</li>
                  </ol>
                </div>
              </CardContent>
            </Card>

            {gscImportResults && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    Import Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">{gscImportResults.imported_count || 0}</p>
                      <p className="text-sm text-green-600">Keywords Imported</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-700">{gscImportResults.skipped_count || 0}</p>
                      <p className="text-sm text-blue-600">Skipped (duplicates)</p>
                    </div>
                  </div>

                  {gscImportResults.sample_entries?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Sample Imported Keywords:</h4>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {gscImportResults.sample_entries.slice(0, 10).map((entry, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                            <span className="truncate flex-1">{entry.keyword}</span>
                            <Badge variant="outline">#{entry.position}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Content Optimization Tab */}
        <TabsContent value="optimize" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5" />
                  Content Optimization
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Content Type</Label>
                  <Select value={contentType} onValueChange={setContentType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blog">Blog Post</SelectItem>
                      <SelectItem value="landing_page">Landing Page</SelectItem>
                      <SelectItem value="product">Product Page</SelectItem>
                      <SelectItem value="category">Category Page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Target Keywords (comma-separated) *</Label>
                  <Input
                    placeholder="keyword1, keyword2"
                    value={optimizeKeywords}
                    onChange={(e) => setOptimizeKeywords(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Content to Optimize *</Label>
                  <Textarea
                    placeholder="Paste your content here..."
                    value={optimizeContent}
                    onChange={(e) => setOptimizeContent(e.target.value)}
                    rows={10}
                  />
                </div>
                <Button onClick={handleContentOptimization} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Get Optimization Suggestions
                </Button>
              </CardContent>
            </Card>

            {optimizationResults && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Optimization Results</CardTitle>
                    <ScoreCircle score={optimizationResults.overall_score} size="sm" />
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[450px] pr-4">
                    <div className="space-y-4">
                      {/* Keyword Analysis */}
                      <div>
                        <h4 className="font-medium mb-2">Keyword Density</h4>
                        <div className="space-y-2">
                          {optimizationResults.keyword_analysis?.map((kw, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 border rounded">
                              <span>{kw.keyword}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">{kw.count}x ({kw.density}%)</span>
                                <Badge className={
                                  kw.status === 'good' ? 'bg-green-100 text-green-700' :
                                  kw.status === 'low' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }>
                                  {kw.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Separator />

                      {/* Suggestions */}
                      {optimizationResults.suggestions?.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2">Suggestions</h4>
                          <div className="space-y-2">
                            {optimizationResults.suggestions.map((sug, idx) => (
                              <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={sug.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                                    {sug.priority}
                                  </Badge>
                                  <span className="font-medium text-sm">{sug.suggestion}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">{sug.action}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AI Suggestions */}
                      {optimizationResults.ai_suggestions?.length > 0 && (
                        <div>
                          <h4 className="font-medium flex items-center gap-2 mb-2">
                            <Sparkles className="h-4 w-4" /> AI Suggestions
                          </h4>
                          <div className="space-y-2">
                            {optimizationResults.ai_suggestions.map((sug, idx) => (
                              <div key={idx} className="p-3 bg-purple-50 rounded-lg text-sm">
                                {sug}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Rank Entry Dialog */}
      <Dialog open={showAddRankDialog} onOpenChange={setShowAddRankDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Rank Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Keyword</Label>
              <Input
                value={newRankEntry.keyword}
                onChange={(e) => setNewRankEntry({...newRankEntry, keyword: e.target.value})}
                placeholder="Enter keyword"
              />
            </div>
            <div className="space-y-2">
              <Label>Position</Label>
              <Input
                type="number"
                value={newRankEntry.position}
                onChange={(e) => setNewRankEntry({...newRankEntry, position: e.target.value})}
                placeholder="1-100"
              />
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                value={newRankEntry.url}
                onChange={(e) => setNewRankEntry({...newRankEntry, url: e.target.value})}
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRankDialog(false)}>Cancel</Button>
            <Button onClick={handleAddRankEntry}>Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Competitor Dialog */}
      <Dialog open={showAddCompetitorDialog} onOpenChange={setShowAddCompetitorDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Competitor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Domain</Label>
              <Input
                value={newCompetitor.domain}
                onChange={(e) => setNewCompetitor({...newCompetitor, domain: e.target.value})}
                placeholder="competitor.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Name (optional)</Label>
              <Input
                value={newCompetitor.name}
                onChange={(e) => setNewCompetitor({...newCompetitor, name: e.target.value})}
                placeholder="Competitor Name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCompetitorDialog(false)}>Cancel</Button>
            <Button onClick={handleAddCompetitor}>Add Competitor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SEOToolsPage;
